import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/opt/atlassian/pipelines/agent/build/platform/services/design-system-docs/src/__DO_NOT_ADD_TO_THIS_FOLDER__/gatsby-theme-brisk/templates/page.tsx";
import buttonAndIcon from './button-and-icon.png';
import buttonSpacingBeforeLight from './button-spacing-before.light.png';
import buttonSpacingAfterLight from './button-spacing-after.light.png';
import buttonSpacingBeforeDark from './button-spacing-before.dark.png';
import buttonSpacingAfterDark from './button-spacing-after.dark.png';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <p>{`The button is a foundational part of UI and every design system. In reviewing how buttons were being
used across Atlassian’s interfaces we realized our trusty button component was trying to do too
much.`}</p>
    <p>{`Enter our new button and link components that improve performance, visual consistency, and make it
easier to create more accessible interfaces by default.`}</p>
    <h2 {...{
      "id": "understanding-the-problem"
    }}>{`Understanding the problem`}</h2>
    <p>{`The first stage was to understand how our button was being used, and the challenges people faced. We
quickly discovered the component itself was large and slow, and there was a lot of visual variation
and customization, as teams weren’t able to build the interfaces they needed using the
one-size-fits-all solution.`}</p>
    <img src={buttonAndIcon} alt="" />
    <h2 {...{
      "id": "building-better-buttons"
    }}>{`Building better buttons`}</h2>
    <p>{`The demand was clear for a wider range of purpose-built buttons that included:`}</p>
    <ul>
      <li parentName="ul">{`icon buttons`}</li>
      <li parentName="ul">{`link and link icon buttons`}</li>
      <li parentName="ul">{`split buttons`}</li>
    </ul>
    <p>{`At the same time as defining new purpose-built solutions, we knew we needed to make sure the code
itself was performant and easier to maintain.`}</p>
    <p>{`To do this, we switched from dynamic to static styling, removed customization APIs, which yielded on
average a 10% reduction in render times. Which seems small on its own, but when considering how
frequently buttons are used across Atlassian, this is a huge upgrade.`}</p>
    <p>{`As a result we’ve had significant performance improvements:`}</p>
    <ul>
      <li parentName="ul">{`10.6% reduction in hydration time`}</li>
      <li parentName="ul">{`9.9% reduction via renderToStaticMarkup`}</li>
      <li parentName="ul">{`22% reduction in SSR via renderToString`}</li>
    </ul>
    <h2 {...{
      "id": "increasing-visual-harmony"
    }}>{`Increasing visual harmony`}</h2>
    <p>{`By aligning on consistent spacing our dropdown buttons and other buttons now look much more
balanced.`}</p>
    <img src={buttonSpacingBeforeLight} srcDark={buttonSpacingBeforeDark} alt="Before the button improvements, the padding either side of buttons with a label and buttons with an icon either to the left or right of a label was 12 pixels. The margin between the label and the icon was 4 pixels." caption="Button spacing before" />
    <img src={buttonSpacingAfterLight} srcDark={buttonSpacingAfterDark} alt="After the button improvements, buttons with an icon on the left of the label has padding of 8 pixels before the icon and 12 pixels to the right of the label. Buttons with an icon on the right of the label has padding of 8 pixels after the icon and 12 pixels to the left of the label. The gaps between a label and icon remains at 4 pixels and the padding either side of a button with a label only remains at 12 pixels on both sides." caption="Button spacing after" />
    <h2 {...{
      "id": "more-accessible-apps-by-default"
    }}>{`More accessible apps by default`}</h2>
    <p>{`We’re always looking at ways to support building more accessible and consistent apps by default, and
so we designed our new API to force a deliberate choice between links, buttons, and links that look
like buttons.`}</p>
    <blockquote>
	The deliberate choice between links and buttons protects you from a11y gotchas.
    </blockquote>
    <p>{`The result is that our new components make it easier to implement semantically correct and
accessible buttons and links.`}</p>
    <h2 {...{
      "id": "migrating-to-our-new-buttons"
    }}>{`Migrating to our new buttons`}</h2>
    <p>{`Of course, designing and coding the new buttons was just the first stage. We needed to migrate
around 2000 callsites in the codebase.`}</p>
    <p>{`In order to roll out our new button we ran a comprehensive campaign, working directly with teams as
well as establishing new lint rules and codemods.`}</p>
    <h2 {...{
      "id": "improved-performance-through-greater-consistency"
    }}>{`Improved performance through greater consistency`}</h2>
    <p>{`The value of the new buttons is clear: they are more accessible and consistent, and semantically
aligned with web standards. And by separating concerns into multiple smaller and purpose-built
components, we’ve reduced the performance cost for teams and customers.`}</p>
    <p>{`Our new buttons are ready and available for everyone to use:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/components/button/examples"
        }}>{`Components - Button`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://go.atlassian.com/figma-libraries"
        }}>{`Figma libraries (Atlassians only)`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://www.figma.com/@atlassian"
        }}>{`Community Figma libraries`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      